import RiotControl from 'riotcontrol'
import logger from '../logger'
import {
  get,
  pick,
  reduce,
  isArray,
  isEqual,
  omit,
  isEmpty,
} from 'lodash-es'
import extendedObservable from './base'
import api from '../api'
import AddressUtils from '../../util/utils/address-utils'
import shippingRestrictionCodes from '../maps/shippingRestrictionCodes'
import appProps from '../app-props'
import shippingIds from '../../util/maps/shippingIds'


const shouldFilterOutNonExpeditedGC = appProps.get('fulfillment.free.expedited.gc.enabled')
const useByEads = appProps.get('checkout.audit.use.by.ead', false)


// //////////////////////////
// Fulfillments Observable //
// //////////////////////////

export function FulfillmentsObservable() {
  const orderResourceKey = 'fulfillments'

  extendedObservable(this, 'fulfillmentsModel')

  let localOrderItems = []
  let fetchState = {
    fetchFailed: false,
    fetchSuccess: false,
    isFetching: false,
  }
  const addressesUsedForFetch = {}

  // NOTE: Special patch used to filter out non-expedited GC options since
  // the REI mobile application is not able to work around data from the BE
  // that does not return the default method.
  //
  // Filters out standard shipping for physical gift cards only when they
  // are eligible for expedited. Non-eligible addresses would be PO Boxes
  // and Military addresses.
  //
  // Likely a candidate for removal when a data/BE centric fix is possible
  // for all clients to absorb.
  const filterOutNonExpeditedGC = (json) => {
    const allowedShipMethods = json.fulfillments['0'].shipMethodsInformation.allowedShipMethods

    if (allowedShipMethods.length > 1) {
      const matchId = allowedShipMethods.findIndex(method => method.id === 'GIFT_CARD_AND_MEMBERSHIP')
      if (matchId >= 0) {
        allowedShipMethods.splice(matchId, 1)
      }
    }
  }

  const mapByGiftShipIds = (method) => {
    const METHODS = {
      STANDARD: 'GIFT_CARD_AND_MEMBERSHIP',
      TWODAY: 'GIFT_CARD_TWO_DAY',
      NEXTDAY: 'GIFT_CARD_EXPRESS',
    }
    return METHODS[method]
  }

  const mapInternationalShipIds = (countryCode) => {
    if (countryCode === 'CA') return 'CANADIAN_AIR'
    return 'INTERNATIONAL_AIR'
  }

  const mapShipIds = (method, countryCode, isGiftCard) => {
    if (isGiftCard) return mapByGiftShipIds(method)
    if (countryCode !== 'US') return mapInternationalShipIds(countryCode)
    const METHODS = {
      STANDARD: 'STANDARD',
      TWODAY: 'TWO_DAY',
      NEXTDAY: 'ONE_DAY_EXPRESS',
    }
    return METHODS[method]
  }

  const mapByResponse = (json) => {
    const mappedResponse = {}

    json.orderLines.forEach((line) => {
      const pickup = line.fulfillmentOptions.find(option => option.fulfillmentType === 'PICK')
      const isPickup = !!pickup
      const countryCode = get(line, 'shipTo.countryCode')
      const isGiftCard = !!get(line, 'skuInformation.skuMediaCode')
      // This maps the data from each fulfillment method and node to match the Sterling structure
      const mappedMethods = line.fulfillmentOptions.map((option) => {
        const serviceLevel = get(option, 'fulfillmentServiceLevel')
        const fulfillmentServiceLevel = isPickup ? serviceLevel : mapShipIds(serviceLevel, countryCode, isGiftCard)
        const rawResponseEad = get(option, 'estimatedArrivalDate');
        const ead = rawResponseEad !== '-1'
          ? Date.parse(rawResponseEad) : -1

        return {
          ead,
          backordered: false, // BY doesn't support backorder yet...
          fulfillmentServiceLevel,
          fulfillmentQty: get(option, 'fulfilledQuantity'),
          shipNode: get(option, 'store.storeNumber'),
          fulfillmentType: get(option, 'fulfillmentType'),
        }
      })

      // We are iterating over the available ship methods and finding all the matching
      // fulfillment nodes and adding them to the fulfillmentInfos property.
      const allowedShipMethods = line.shipMethodsInformation.allowedShipMethods.map((method) => {
        const id = shippingIds[method.id]
        const fulfillmentInfos = isPickup
          ? mappedMethods.filter(info => info.fulfillmentType === 'PICK')
          : mappedMethods.filter(info => info.fulfillmentServiceLevel === id)

        return {
          id,
          fulfillmentInfos,
          description: method.description,
          shippingCharge: method.shippingCharge,
          oversizeShippingCharge: method.oversizeShippingCharge,
        }
      })


      // The result of the above parsing is that the ship methods might not be in the correct order
      allowedShipMethods.sort((a, b) => {
        if (b.fulfillmentInfos.length && a.fulfillmentInfos.length) {
          return b.fulfillmentInfos[0].ead - a.fulfillmentInfos[0].ead
        }
        return 0
      })

      mappedResponse[line.orderLineId] = {
        orderLineId: line.orderLineId,
        skuInformation: line.skuInformation,
        quantity: line.quantity,
        fulfillments: [{
          fulfillmentType: isPickup ? 'PICKUP' : 'SHIP',
          shipTo: {
            city: line.shipTo.city,
            stateCode: line.shipTo.state,
            countryCode: line.shipTo.countryCode,
            postalCode: line.shipTo.postalCode,
            addressLine1: '',
            addressLine2: '',
            // Conditionally add the properties below if it's pickup
            ...(isPickup && {
              storeDisplayName: get(pickup, 'store.storeDisplayName'),
              storeId: line.storeNumber,
              storeTimeZoneId: get(pickup, 'store.timeZoneId'),
            }),
          },
          shipMethodsInformation: {
            allowedShipMethods,
            restrictions: line.shipMethodsInformation.shippingRestrictionMessage ? [{
              code: 106,
              message: line.shipMethodsInformation.shippingRestrictionMessage,
            }] : [],
            informationalMessages: line.shipMethodsInformation.shippingInformationMessage ? [line.shipMethodsInformation.shippingInformationMessage] : [],
          },
          ...(isPickup && {
            pickupStoreRawSellableInventory: {
              location: line.storeNumber,
              quantity: line.quantity,
              sku: line.sku,
            },
          }),
        }],
      }
    })

    return mappedResponse
  }

  const successHandler = (json) => {
    logger('SUCCESS: Communicated with OP Fulfillment Service - ', 'info')()
    logger(json, 'info')()

    // If special rules to enable free expedited are in play
    // strip out standard (when appropriate)
    if (shouldFilterOutNonExpeditedGC) filterOutNonExpeditedGC(json)

    this.model[json.orderLineId] = json

    fetchState.fetchFailed = false

    RiotControl.trigger('updateOrderResource', orderResourceKey, this.model)

    return this.model
  }

  // Merge objects preserving accessors
  function mergeGetters(target, source) {
    const result = Object.defineProperties(
      {},
      {
        ...Object.getOwnPropertyDescriptors(target),
        ...Object.getOwnPropertyDescriptors(source),
      }
    )
    return result
  }

  const bySuccessHandler = (json) => {
    logger('SUCCESS: Communicated with OP Fulfillment Service - ', 'info')()
    logger(json, 'info')()

    const mappedResponse = mapByResponse(json)

    // If special rules to enable free expedited are in play
    // strip out standard (when appropriate)
    if (shouldFilterOutNonExpeditedGC) {
      // eslint-disable-next-line
      for (const [key, value] of Object.entries(mappedResponse)) {
        filterOutNonExpeditedGC(value)
      }
    }

    this.model = mergeGetters(this.model, mappedResponse)

    fetchState.fetchFailed = false

    RiotControl.trigger('updateOrderResource', orderResourceKey, this.model)

    return this.model
  }

  const errorHandler = (xhr, status, errorThrown) => {
    logger(`ERROR: Failed to communicate with OP Fulfillment Service - ${errorThrown}`, 'error')()
    logger(`Status: ${status}`, 'error')()
    logger(xhr, 'error')()

    fetchState.fetchFailed = true

    return xhr
  }

  // ////////////
  // Singular //
  // ////////////
  const getFulfillment = data => new Promise((resolve, reject) => {
    const options = {
      isEadIncluded: true,
    }
    if (this.orderModel.safeGet('customerInfo.customerType', '') === 'E') options.customerType = 'E'
    api.fullfillment.post(data, options)
      .then(successHandler)
      .then(resolve)
      .fail(errorHandler)
      .fail(reject)
  })

  /**
   * This creates the appropriate payload to send along to the
   * fulfillment service. It will determine if the user has set
   * a billing shipping address or not. By default the user will
   * only have a billing address entered so this will build the
   * request accordingly. As we know more, e.g. is the user enters
   * a shipping address, then we will use that in favor of the
   * shipping address.
   * @param  {Object} data a single line item object
   * @return {Object}      payload for the fulfillment service
   */
  const createPayload = data => new Promise((resolve) => {
    // or the shipping address. In the case where there is no address, then we need
    // to not send these requests until we have one

    let fulfillmentType
    if (get(data.item, 'shippingAddress.address.storeId')) {
      // item for pickup
      fulfillmentType = 'PICKUP'
    } else {
      fulfillmentType = 'SHIP'
    }

    const mappedData = {
      orderLineId: get(data.item, 'reiRefId'),
      skuInformation: {
        skuNumber: get(data.item, 'sku'),
        skuMediaCode: get(data.item, 'media.code'),
        shippingTier: get(data.item, 'shippingTier'),
        storeFulfillable: get(data.item, 'isRSPUable'),
      },
      quantity: get(data.item, 'quantity'),
      fulfillments: [{
        fulfillmentType,
        shipTo: data.address,
      }],
    }

    resolve(JSON.stringify(mappedData))
  })

  const isValidAddress = (address) => {
    // fail fast
    if (!address) return false

    const required = [
      'city',
      'stateCode',
      'countryCode',
      'postalCode',
      'line1',
    ]

    // make sure the address contains the required fields
    // and that they are not null or undefined
    return required.some(value => get(address, value) != null)
  }

  /**
   * Used to create a standard for comparing addresses
   * Only compare on 'city', 'stateCode', 'countryCode', 'postalCode', 'line1', and 'line2'
   * @param  {Object} address the address to be pruned
   * @return {Object}         the pruned address
   */
  const pruneAddress = address => pick(address,
    'city',
    'stateCode',
    'countryCode',
    'postalCode',
    'line1',
    'line2'
  )

  /**
   * Returns the correct address to use for the call to the fulfillment service
   * @param  {Object} orderModel The order object
   * @return {Object}           Address object
   */
  const determineFulfillmentAddress = (item, orderModel) => new Promise((resolve, reject) => {
    // address preference order:
    // shipTo
    // billTo
    // geoIP?

    let address
    let useBilling = false
    const billingAddress = orderModel.safeGet('billingAddress.address')
    let shippingAddress = get(item, 'shippingAddress.address') ? get(item, 'shippingAddress.address') : get(orderModel.safeGet('getters.getOrderShippingAddress'), 'address')

    // return shipping address
    // SHIP, PICKUP or null
    // either return the address or if it is set to null, then we default to SHIP

    // if shipping
    //   if has shipping address
    //   else billing
    // if pickup
    //   shipping address

    // No address to make the request with, return
    if (!billingAddress && !shippingAddress) {
      reject('Cannot check fulfillment - no address')
      return
    }

    if (!isValidAddress(billingAddress) && !isValidAddress(shippingAddress)) {
      reject('Cannot check fulfillment - incomplete address')
      return
    }

    if (get(item, 'shipMethod.fulfillmentType') === 'SHIP'
      || get(item, 'shippingAddress') === null
      || (get(item, 'shippingAddress') && get(item, 'shippingAddress.address.storeId') === null)) {
      // this item is being shipped
      // Check for membership card, ONLY use billing address in that case.
      if (billingAddress && get(item, 'sku') === '6485920018') shippingAddress = billingAddress

      // ensure that we only use shipping address or
      // billing address in whole
      if (isValidAddress(shippingAddress)) {
        useBilling = false

        address = {
          city: get(shippingAddress, 'city'),
          stateCode: get(shippingAddress, 'stateCode'),
          countryCode: get(shippingAddress, 'countryCode'),
          postalCode: get(shippingAddress, 'postalCode'),
          addressLine1: get(shippingAddress, 'line1'),
          addressLine2: get(shippingAddress, 'line2'),
        }
      } else {
        useBilling = true

        address = {
          city: get(billingAddress, 'city'),
          stateCode: get(billingAddress, 'stateCode'),
          countryCode: get(billingAddress, 'countryCode'),
          postalCode: get(billingAddress, 'postalCode'),
          addressLine1: get(billingAddress, 'line1'),
          addressLine2: get(billingAddress, 'line2'),
        }
      }
    } else {
      // this item is being picked up. Pickup items
      // will always have a store address attached as
      // a `shippingAddress`, so use that.
      address = {
        city: get(item, 'shippingAddress.address.city'),
        stateCode: get(item, 'shippingAddress.address.stateCode'),
        countryCode: get(item, 'shippingAddress.address.countryCode'),
        postalCode: get(item, 'shippingAddress.address.postalCode'),
        addressLine1: get(item, 'shippingAddress.address.line1'),
        addressLine2: get(item, 'shippingAddress.address.line2'),
        storeId: get(item, 'shippingAddress.address.storeId'),
      }
      // Set this to a variable so we can use it for comparison later.
      addressesUsedForFetch[item.reiRefId] = useBilling ?
        pruneAddress(billingAddress) : pruneAddress(shippingAddress)
    }

    resolve({
      item,
      address,
    })
  })

  const getFulfillmentForItem = (item, data, cb) =>
    new Promise((resolve, reject) => determineFulfillmentAddress(item, data)
      .then(createPayload)
      .then(getFulfillment)
      .then(this.notifyObservers)
      .then(cb)
      .then(resolve)
      .catch((err) => {
        logger(err, 'debug')()
        reject(err)
      }))

  const reduceLineItems = lineItems =>
    reduce(lineItems, (res, value) => {
      res.push({
        reiRefId: value.reiRefId,
        sku: value.sku,
        quantity: value.quantity,
        shippingAddress: {
          address: pruneAddress(get(value, 'shippingAddress.address')),
        },
      })

      return res
    }, [])

  /**
   * function used to determine if fulfillments should be retrieved or not
   * @param  {Object} data The orderModel coming in from the `update` event
   * @return {Boolean}      Yes/No - should fetch
   */
  const shouldFetchFulfillment = (data) => {
    const orderModel = get(data, 'orderModel')
    const lItems = orderModel.safeGet('lineItems')

    // Don't fetch if there is a fetch in flight
    if (fetchState.isFetching) return false

    // Are there lineItems?
    // No? Don't fetch
    if (isArray(lItems) && !lItems.length) return false

    // Did the fetch fail before?
    // fetch
    // Short circuit, because we may want to force a re-fetch if it
    // failed for some reason.
    if (fetchState.fetchFailed && !fetchState.isFetching) return true

    // We need to have a clean way to compare items on the order. Because
    // the line items are somewhat mutable, we compare on the data most
    // important to deciding if a call to fulfillments is necessary:
    // quantity, reiRefId shippingAddress (minus 'validation' property) and sku
    const reducedLineItems = reduceLineItems(lItems)

    // Do we have a billing address (and no shipping address) to fetch?
    // Did we fetch with billing already?
    // No? fetch
    const hasShipItems = !!this.orderModel.safeGet('getters.filteredShipLineItemsWithGC').length
    const billingAddress = orderModel.safeGet('billingAddress.address', {})
    const shippingAddress = orderModel.safeGet('getters.getOrderShippingAddress')
    if (hasShipItems && AddressUtils.isReadyForScrubbing(billingAddress) && !shippingAddress) {
      const isSameBillingAddress = reducedLineItems.some(item =>
        isEqual(pruneAddress(billingAddress), addressesUsedForFetch[item.reiRefId]))

      if (!isSameBillingAddress) return true

      // else, the address is the same as the last
      // fetch, no need to re-fetch
      return false
    }

    // Check to see if the address we used to fetch fulfillment information is the same as the
    // current shipping address for each line item. If it is there's no need to fetch the
    // fulfillment data again
    if (reducedLineItems.length) {
      let addressesMatch = false

      // eslint-disable-next-line no-plusplus
      for (let i = 0, l = reducedLineItems.length; i < l; i++) {
        const item = reducedLineItems[i]
        const fulfillmentAddress = get(this.model[item.reiRefId], 'fulfillments[0].shipTo')
        let itemAddress = omit(get(item, 'shippingAddress.address', {}), 'line2')
        // If there is no ship address but there is a billing address use billing
        if (isEmpty(itemAddress) && billingAddress) {
          itemAddress = {
            line1: billingAddress.line1,
            city: billingAddress.city,
            countryCode: billingAddress.countryCode,
            postalCode: billingAddress.postalCode,
            stateCode: billingAddress.stateCode,
          }
        }

        if (fulfillmentAddress) {
          // The keys are different for fulfillment address so we need to map it to the same keys
          // as the item address to compare
          const fulfillmentMapped = {
            line1: fulfillmentAddress.addressLine1,
            city: fulfillmentAddress.city,
            countryCode: fulfillmentAddress.countryCode,
            postalCode: fulfillmentAddress.postalCode,
            stateCode: fulfillmentAddress.stateCode,
          }

          // We don't currently have a mechanism to just fetch fulfillment for one line item so
          // if any of them don't match we just re-fetch fulfillment
          if (!isEqual(fulfillmentMapped, itemAddress)) {
            addressesMatch = false
            break
          }

          addressesMatch = true
        }
      }

      if (addressesMatch) return false
    }

    // Are the new line items the exact same as the old (local) lineItems?
    // Don't fetch
    if (isEqual(localOrderItems, reducedLineItems)) return false

    // OK, it looks like we should actually go and fetch :)
    // Set the local items for comparison later.
    localOrderItems = reducedLineItems

    return true
  }

  const createByPayload = async (orderModel, lineItems) => {
    const orderLines = await Promise.all(lineItems.map(async (item) => {
      const { address } = await determineFulfillmentAddress(item, orderModel)
      const countryCode = get(address, 'countryCode')
      const postalCode = get(address, 'postalCode', '') || ''

      return {
        orderLineId: item.reiRefId,
        sku: item.sku,
        quantity: item.quantity,
        shipTo: {
          addressLine1: get(address, 'addressLine1'),
          addressLine2: get(address, 'addressLine2'),
          countryCode,
          state: get(address, 'stateCode'),
          city: get(address, 'city'),
          postalCode: countryCode === 'US' ? postalCode.slice(0, 5) : postalCode,
          latitude: '',
          longitude: '',
        },
        fulfillmentType: item.shipMethod.fulfillmentType === 'PICKUP' ? 'PICK' : 'SHIP',
        skuInformation: {
          skuNumber: item.sku,
          skuMediaCode: get(item, 'giftCardDetails.mediaCode', ''),
          totalAdjustedPrice: item.price.totalAdjusted,
          totalOriginalPrice: item.price.totalOriginal,
          totalPrice: item.price.total,
        },
        storeNumber: address.storeId,
        fulfillmentOptions: [],
      }
    }))

    return {
      orderLines,
      orderId: orderModel.externalOrderId,
      customerType: orderModel.customerInfo.customerType,
    }
  }

  const getByFulfillments = payload => new Promise((resolve, reject) =>
    api.availability.post(payload)
      .then(bySuccessHandler)
      .then(resolve)
      .fail(errorHandler)
      .fail(reject))

  const updateFulfillments = async (orderModel, lineItems) => {
    const items = lineItems || orderModel.safeGet('lineItems', [])

    fetchState.isFetching = true
    RiotControl.trigger('fetchingFulfillments')

    if (useByEads) {
      const fddItems = lineItems || orderModel.safeGet('getters.filteredFulfillmentItems', [])
      return updateByFulfillments(orderModel, fddItems)
    }

    return updateSterlingFulfillments(orderModel, items)
  }

  const updateByFulfillments = async (orderModel, lineItems) => {
    const payload = await createByPayload(orderModel, lineItems)
    // If it's an international order use Sterling until for now
    return new Promise((resolve, reject) => getByFulfillments(payload)
      .then(updateFulfillmentsSuccess)
      .then(resolve)
      .catch((err) => {
        updateFulfillmentsError(err)
        reject(err)
      }))
  }

  const updateSterlingFulfillments = (orderModel, lineItems) => {
    const requests = []

    // Logger for excessive calls. Would
    // be nice to hook this into some analytics...
    if (lineItems.length > 100) logger('Item count exceeds 100!', 'warning')()

    lineItems.forEach((item) => {
      requests.push(getFulfillmentForItem(item, orderModel))
    })

    // update the fulfillment data and trigger for new shipping charges
    // wait for all of the fulfillment requests to return.
    return Promise.all(requests)
      .then(updateFulfillmentsSuccess)
      .catch(updateFulfillmentsError)
  }

  const updateFulfillmentsSuccess = () => {
    logger('SUCCESS: All the fulfillment information retrieved.', 'info')()

    RiotControl.trigger('fetchingFulfillmentsComplete')
    RiotControl.trigger('fetchingFulfillmentsSuccess')
    this.notifyObservers({ event: 'fulfillmentsReady' })

    if (!useByEads) {
      RiotControl.trigger('getShippingCharges', {
        orderModel: this.orderModel,
        lineItems: this.orderModel.safeGet('lineItems', []),
      })

      RiotControl.one('shippingChargeSuccess', () => {
        fetchState.fetchSuccess = true
        fetchState.isFetching = false
      })

      RiotControl.one('shippingChargeFailure', () => {
        fetchState.fetchSuccess = true
        fetchState.isFetching = false
      })
    }
  }

  const updateFulfillmentsError = (reason) => {
    // there was some error in the chain, should fetch again once this is triggered...
    fetchState.fetchFailed = true
    fetchState.isFetching = false
    RiotControl.trigger('fetchingFulfillmentsComplete')
    RiotControl.trigger('fetchingFulfillmentsFailure')
    logger(reason, 'info')()
  }

  /**
   * This property is designed to return Boolean if there are ANY restrictions
   * on the returned fulfillments. this is then used to trigger a state in the
   * UI so that we can alert our users to the fact that restrictions exist on
   * the order.
   * @param  {Object} this.model                   The fulfillments model
   * @param  {String} 'hasAnyShippingRestrictions' Name of the getter property
   * @return {Boolean}                             If there are ANY restrictions or not
   */
  Object.defineProperty(this.model, 'hasAnyShippingRestrictions', {
    get: () => {
      logger('hasAnyShippingRestrictions', 'debug')()

      let hasRestrictions = false
      // eslint-disable-next-line no-restricted-syntax
      for (const [key] of Object.entries(this.model)) {
        if ((get(this.model[key], 'fulfillments["0"].shipMethodsInformation.restrictions', []) || []).length > 0
            && (shippingRestrictionCodes[get(this.model[key], 'fulfillments["0"].shipMethodsInformation.restrictions["0"].code')])
        ) {
          hasRestrictions = true
          break
        }
      }
      return hasRestrictions
    },
    enumerable: false,
    configurable: false,
  })

  this.on('registerFulfillmentsObserver', observer => this.registerObserver(observer))
  this.on('unregisterFulfillmentsObserver', observer => this.unregisterObserver(observer))

  // ///////////
  // Singular //
  // ///////////
  this.on('getFulfillment', (item, data, cb) => {
    logger('getFulfillment', 'debug')()
    getFulfillmentForItem(item, data, cb)
  })

  this.on('resetFulfillment', () => {
    fetchState = {
      fetchFailed: false,
      fetchSuccess: false,
      isFetching: false,
    }
  })

  this.on('removeFromFulfillmentModel', (data) => {
    delete this.model[data]
  })

  this.on('shippingAddressChanged', () => {
    logger('FULFILLMENT:: Shipping information changed. Updating fulfillment.', 'info')()
    updateFulfillments(this.orderModel)
  })

  this.on('fetchFulfillments', (cb) => {
    logger('FULFILLMENT:: Force refresh of fulfillments triggered.', 'info')
    updateFulfillments(this.orderModel).then(cb)
  })

  // The purpose of this is so when a user provides a new address we can fetch the fulfillments
  // and ship charges without applying the address to the order. The reason we want to do this
  // is so that we can update the address and ship method with one line-items request
  this.on('fetchFulfillmentsForAddress', (lineItems, cb) => {
    logger('FULFILLMENT:: Force refresh of fulfillments triggered.', 'info')
    updateFulfillments(this.orderModel, lineItems)
      .then(() => {
        // Update localOrderItems so FS isn't called again after we update the address and ship
        // method. Note that `lineItems` is ship line items, it does not include membership or
        // pick up items
        localOrderItems = reduceLineItems(lineItems)
      })
      .then(cb)
      .catch(err => {
        logger(err, 'error')()
      })
  })

  // NOTE: Fulfillment information is fetched (per item) on these conditions:
  // - Initial application load
  // - The shipping address changes.
  // - The actual number of line items changes
  //
  // That being the case, even though fulfillments are fetched per item, we need to wait until
  // all requests have returned to then do a subsequent request to get the shipping charge
  // information. There could still be a use case that I am unaware of where an individual item's
  // fulfillment information will need to be requested individually, but in that case I am not
  // sure how to compensate for the subsequent request that happens as a result. For now, when
  // the main request goes out the observable will keep track of a promise chain and fire the
  // subsequent logic on the finish of those individual requests to the fulfillment service.

  this.on('update', (data) => {
    // check to see if the orderLines are the same as before
    const orderModel = get(data, 'orderModel')

    if (shouldFetchFulfillment(data)) updateFulfillments(orderModel)
  })
}
